// ===========================================
// Extra Lesson Metrics Cards Component
// ===========================================
// Summary cards showing extra lesson request metrics

import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  EventAvailable as RequestIcon,
  CheckCircle as ApprovedIcon,
  Schedule as PendingIcon,
  Speed as TimeIcon,
} from '@mui/icons-material';

import { ExtraLessonMetrics } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface ExtraLessonMetricsCardsProps {
  data: ExtraLessonMetrics | undefined;
  loading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function ExtraLessonMetricsCards({ data, loading }: ExtraLessonMetricsCardsProps) {
  // Loading state
  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  // Empty state
  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No extra lesson metrics available for the selected period.
      </Alert>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Total Requests Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'white',
                  mr: 1.5,
                }}
              >
                <RequestIcon fontSize="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Requests
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {data.totalRequests}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.approvedCount} approved, {data.rejectedCount} rejected
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Approval Rate Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#5cb399',
                  color: 'white',
                  mr: 1.5,
                }}
              >
                <ApprovedIcon fontSize="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Approval Rate
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {data.approvalRate.toFixed(1)}%
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Box
                sx={{
                  width: '100%',
                  height: 6,
                  bgcolor: 'grey.200',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${data.approvalRate}%`,
                    height: '100%',
                    bgcolor: '#5cb399',
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Average Approval Time Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#FFCE00',
                  color: '#080808',
                  mr: 1.5,
                }}
              >
                <TimeIcon fontSize="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Avg Approval Time
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {data.avgApprovalTimeHours.toFixed(1)}h
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.avgApprovalTimeHours < 24 ? 'Same day' : `${Math.floor(data.avgApprovalTimeHours / 24)} days`}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Pending Requests Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#FFAE9E',
                  color: '#080808',
                  mr: 1.5,
                }}
              >
                <PendingIcon fontSize="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Pending Requests
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {data.pendingCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Awaiting review
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
